<script lang="ts">
	import Progress from "../components/Progress.svelte";
	import Button from "../components/Button.svelte";
	import BackIcon from "../components/icon/Back.svelte";
	import ClickDimensionsForm from "./ClickDimensionsForm.svelte";
	import SvelteMarkdown from "svelte-markdown";

	import {
		AssessmentNode,
		AssessmentNodeType,
	} from "./models/AssessmentNode";
	import { fade } from "svelte/transition";
	import { afterUpdate, beforeUpdate, onMount } from "svelte";
	import { defaults } from "./data/selfAssessmentDefaultOptions";
	import { filterObject } from "../util/object";
	import { getScrollParent } from "../util/dom";
	import ClickDimensionsAdviceForm from "./ClickDimensionsAdviceForm.svelte";
	// import { rootAssessmentNode } from "./data/assessmentTree";

	const options = Object.assign(
		{},
		defaults,
		filterObject((window as any).satOptions) || {},
	);

	export let node: AssessmentNode | null = null;

	const emptyNode = new AssessmentNode();

	let rootNode = emptyNode;
	let currentNode = emptyNode;
	let levels = currentNode?.getMaxDepth() || 0;
	let qnas: string[] = [];
	let loading = true;
	let container: HTMLDivElement | null = null;
	let debug = {};
	let selectedLang = "sv";
	let nextNode: AssessmentNode = emptyNode;
	let showThanks: boolean = false;

	const langs = ["sv", "dk"];

	$: levels = currentNode?.getMaxDepth(true) || 0;
	$: title = currentNode?.title || rootNode?.title;
	$: progress = currentNode?.getAscent() || 0;
	$: {
		nextNode = currentNode.children[0];
	}

	$: if (options.debug) {
		if (
			currentNode.children.length !== 1 &&
			currentNode.type !== AssessmentNodeType.Redirect
		) {
			debug = {};
		} else {
			if (currentNode.type === AssessmentNodeType.Redirect) {
				const { url } = currentNode;
				debug = { type: "Redirect", url };
			} else {
				const result = currentNode.children[0];
				const { price, title, preamble, question, text } = result;
				debug = { title, price, preamble, text, question };
			}
		}
	}

	function handlNodeChoice(event: CustomEvent<AssessmentNode>) {
		const eventNode = event.detail;
		let nextNode: AssessmentNode = eventNode;

		if (
			nextNode.type === AssessmentNodeType.Reference &&
			nextNode.parent !== null
		) {
			nextNode = nextNode.parent.children[nextNode.index];
		}

		if (
			nextNode.type === AssessmentNodeType.Redirect &&
			!!nextNode.url &&
			!options.debug
		) {
			window.location.href = nextNode.url;
			return;
		}

		if (currentNode.type === AssessmentNodeType.Question) {
			qnas = [...qnas, currentNode.question, eventNode.answer];
		}

		if (!!(window as any).dataLayer) {
			if (currentNode.type === AssessmentNodeType.Question) {
				((window as any).dataLayer as any[]).push({
					event: "selfAssessmentAnswer",
					question: currentNode.question,
					answer: nextNode.answer,
				});
			}

			if (nextNode.eventName && nextNode.eventValue) {
				((window as any).dataLayer as any[]).push({
					event: nextNode.eventName,
					[nextNode.eventValueName || "value"]: nextNode.eventValue,
				});
			}
		}

		currentNode = nextNode;
	}

	function handleBack() {
		// remove previous question and answer
		qnas.splice(qnas.length - 2, 2);
		qnas = [...qnas];
		currentNode = currentNode.parent || currentNode;
	}

	function onSuccess() {
		const nextNode: AssessmentNode = currentNode.children[0];

		if (nextNode.type === AssessmentNodeType.Redirect && !!nextNode.url) {
			window.location.href = nextNode.url;
			return;
		} else if (
			options.gated &&
			currentNode.type === AssessmentNodeType.Form &&
			nextNode.type === AssessmentNodeType.Result
		) {
			showThanks = true;
			return;
		}

		currentNode = nextNode;
	}

	function createNode(c: AssessmentNode): AssessmentNode {
		return new AssessmentNode({
			...c,
			children: c.children && c.children.map(createNode),
		});
	}

	async function loadJson(url?: string) {
		if (node) return;

		const res = await fetch(url || options.jsonUrl);

		if (res.ok) {
			const json = await res.json();
			const loadedNode = createNode(json);
			currentNode = loadedNode;
			rootNode = loadedNode;
		}

		qnas = [];
		debug = {};

		loading = false;
	}

	onMount(loadJson);

	beforeUpdate(() => {
		if (node && node !== rootNode) {
			rootNode = node;
			currentNode = node;
			loading = false;
		}
	});

	afterUpdate(() => {
		if (container === null) return;

		const scrollContainer = getScrollParent(container);
		if (
			scrollContainer &&
			scrollContainer.scrollTop > container.offsetTop
		) {
			scrollContainer.scrollTop = container.offsetTop;
		}
	});

	function handleLangChange() {
		loading = true;
		loadJson(`/json/sat.${selectedLang}.json`);
	}
</script>

<div bind:this={container} class="self-assessment-block-container">
	{#if !loading}
		{#if options.gated && nextNode?.type === AssessmentNodeType.Result && currentNode.type === AssessmentNodeType.Form}
			<div id="flex-row-wrapper" class="tp_flex tp_flex-row">
				<div in:fade class="tp_flex tp_flex-row">
					<div class="self-assessment-block-item result">
						<div class="row result-price">
							<h1>
								{nextNode.price?.split("/")[0]} /
								<span class="month"
									>{nextNode.price?.split("/")[1]}</span
								>
							</h1>
						</div>
						<div class="row result-price">
							<h3>{nextNode.preamble}</h3>
						</div>
						<div class="row result-price">
							<h2>{nextNode.title}</h2>
						</div>
						<div class="row result-price">
							<SvelteMarkdown source={nextNode.text} />
						</div>
					</div>
					{#if !showThanks}
						<div class="CD_advice_container">
							<ClickDimensionsAdviceForm
								{options}
								{onSuccess}
								{qnas}
							/>
						</div>
					{:else}
						<div class="show-thanks">
							<SvelteMarkdown source={options.sendSuccessText} />
						</div>
					{/if}
				</div>
			</div>
		{:else if currentNode.type === AssessmentNodeType.Result}
			<div in:fade class="self-assessment-block-item result">
				<div class="row">
					<h1>
						{currentNode.price?.split("/")[0]} /
						<span class="month"
							>{currentNode.price?.split("/")[1]}</span
						>
					</h1>
				</div>
				<div class="row">
					<h3>{currentNode.preamble}</h3>
				</div>
				<div class="row">
					<h2>{currentNode.title}</h2>
				</div>
				<div class="row">
					<SvelteMarkdown source={currentNode.text} />
				</div>
			</div>
		{:else}
			<div class="self-assessment-block-item">
				<div class="row">
					{#key title}
						<h2 in:fade>{title}</h2>
					{/key}
				</div>
				<div class="row">
					<Progress max={levels} value={progress} />
				</div>
				<div class="row">
					{#key currentNode.question}
						<h3 in:fade class="question">
							{currentNode.question}
						</h3>
					{/key}
				</div>
				{#if currentNode.type === AssessmentNodeType.Form}
					<div class="row">
						<ClickDimensionsForm {options} {onSuccess} {qnas} />
					</div>
				{:else}
					<div class="row">
						{#each currentNode.children as child}
							<div class="option">
								<Button
									on:click={handlNodeChoice}
									value={child}
									text={child.answer}
								/>
							</div>
						{/each}
					</div>
				{/if}
				<div class="row">
					{#if (currentNode.parent !== null && currentNode.children.length !== 0) || currentNode.type === AssessmentNodeType.Redirect}
						<button class="back" on:click={handleBack}>
							<BackIcon />
							<span>{options.backText}</span>
						</button>
					{/if}
				</div>
			</div>
		{/if}
	{/if}
	{#if options.debug}
		<div>
			<select bind:value={selectedLang} on:change={handleLangChange}>
				{#each langs as lang}
					<option value={lang}>
						{lang}
					</option>
				{/each}
			</select>
			<pre>
      {qnas.join("\n")}
        {#if !!debug}
					{JSON.stringify(debug, null, 2)}
				{/if}
      </pre>
		</div>
	{/if}
</div>

<style lang="scss">
	:global(html) {
		scroll-behavior: smooth;
	}
	:global(.self-assessment-block-container a) {
		color: #ffffff !important;
		text-decoration: underline;
	}

	.self-assessment-block-container {
		display: flex;
		flex-direction: column;
		background-color: #0a1f2e;
		background-image: url("/images/brand/NBP_Perspective_9_RGB_1.png");
		background-position: bottom center;
		background-size: cover;
		background-repeat: no-repeat;
		color: #ffffff;
		min-height: 480px;
		margin: 1.5rem 0;
		padding: 4rem 3rem;
		box-sizing: border-box;

		> * {
			box-sizing: border-box;
		}

		.tp_flex {
			display: flex;
		}
		#flex-row-wrapper {
			max-width: 1676px;
		}
		.tp_flex-row {
			flex-direction: row;

			@media screen and (max-width: 980px) {
				flex-direction: column;
			}
		}
	}

	.self-assessment-block-item {
		max-width: 1600px;
		margin: auto auto;
		text-align: center;
		flex: 1 1 100%;
		width: 100%;
		display: flex;
		flex-direction: column;
		justify-content: flex-start;

		h3,
		h2 {
			margin-bottom: 2rem;
		}

		.result-price {
			display: flex;
			justify-content: flex-start;
			text-align: start;
			h1 {
				font-size: var(--font__size--36);
				font-family: var(--font__bold);
			}

			span {
				font-size: var(--font__size--24);
			}

			.row {
				justify-content: flex-start;
			}
		}
	}

	.CD_advice_container {
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		min-width: 415px;
		margin-left: 1rem;
		width: 415px;

		@media screen and (max-width: 980px) {
			width: auto;
			min-width: auto;
			margin-top: 1rem;
		}
	}

	:global(.self-assessment-block-item.result p) {
		max-width: 1005px;
		font-size: var(--font__size--24);
	}

	span.month {
		font-size: var(--font__size--24);
		font-family: var(--font__light);
	}

	.back {
		display: flex;
		align-items: center;
		color: #ffffff;
		text-decoration: none;
		cursor: pointer;
		background: none;
		border: none;
		outline: none;

		span {
			margin-left: 0.5rem;
			text-decoration: underline;
		}
	}

	h3.question {
		margin-top: 1rem;
		font-family: var(--font__regular);
	}

	.row {
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		justify-content: center;

		@media screen and (max-width: 980px) {
			margin-bottom: 1rem;
		}

		&:not(:last-child) {
			margin-bottom: 1rem;
		}

		.option {
			margin: 0 1rem 1rem 0;
		}
	}

	pre {
		max-width: 500px;
	}
	.show-thanks {
		min-width: 415px;
		width: 415px;
	}
</style>
